import React from "react";
import { StatCard, ChangeCard } from "../components/Shared";
import { timeAgo } from "../data";

export default function Dashboard({ data, manifest, loading, onRefresh }) {
  const changes = data?.changes_detected || [];
  const immediate = changes.filter((c) => c.classification?.urgency === "immediate");
  const shortTerm = changes.filter((c) => c.classification?.urgency === "short_term");
  const scraped = data?.competitors_scraped || 0;
  const errors = data?.errors || [];
  const pipelineTime = manifest?.sources?.pipeline?.time || data?.completed_at;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400 text-lg">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <h1 className="page-title">Competitor Intelligence</h1>
        <p className="page-subtitle">
          {pipelineTime ? `Last pipeline run ${timeAgo(pipelineTime)}` : "No pipeline data yet"}
          {data?.run_id && <span className="ml-2 font-mono">· #{data.run_id.slice(-8)}</span>}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="👥"
          value={`${scraped}/19`}
          label="Competitors"
          sub={scraped < 19 ? `${19 - scraped} not scraped` : "All tracked"}
          color={scraped >= 14 ? "green" : "amber"}
        />
        <StatCard
          icon="🔔"
          value={changes.length}
          label="Changes detected"
          sub={changes.length > 0 ? `${immediate.length} urgent` : "No changes"}
          color={immediate.length > 0 ? "red" : changes.length > 0 ? "amber" : "green"}
        />
        <StatCard
          icon="🔴"
          value={immediate.length}
          label="Urgent alerts"
          sub={immediate.length > 0 ? "Requires attention" : "All clear"}
          color={immediate.length > 0 ? "red" : "green"}
        />
        <StatCard
          icon="💰"
          value={data?.pricing_extracted || "—"}
          label="Pricing snapshots"
          sub={errors.length > 0 ? `${errors.length} errors` : "Clean run"}
          color={errors.length > 0 ? "amber" : "purple"}
        />
      </div>

      {/* Pipeline status card */}
      {data && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-semibold text-gray-900">Pipeline Status</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-xs text-gray-400 block">Run ID</span>
                <span className="font-mono text-xs">{(data.run_id || "—").slice(-12)}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Status</span>
                <span className={`font-medium ${errors.length > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {errors.length > 0 ? `⚠️ ${errors.length} error${errors.length > 1 ? "s" : ""}` : "✅ Clean"}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Scraped</span>
                <span className="font-medium">{scraped} / 19</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Changes</span>
                <span className="font-medium">{changes.length}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Alerts sent</span>
                <span className="font-medium">{data.alerts_sent || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="card border-red-200 bg-red-50">
          <div className="card-body">
            <h3 className="text-sm font-semibold text-red-800 mb-2">⚠️ {errors.length} Error{errors.length > 1 ? "s" : ""}</h3>
            <ul className="space-y-1">
              {errors.slice(0, 5).map((e, i) => (
                <li key={i} className="text-xs text-red-700 font-mono">{e}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Recent changes */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            Recent Changes
            {changes.length > 0 && <span className="text-sm font-normal text-gray-400 ml-2">({changes.length})</span>}
          </h2>
          {onRefresh && (
            <button onClick={onRefresh} className="text-xs text-blue-600 hover:text-blue-800">
              Refresh
            </button>
          )}
        </div>

        {changes.length === 0 ? (
          <div className="empty-state">
            <p className="text-lg mb-1">No changes detected</p>
            <p className="text-sm">Pipeline ran clean — no competitor content changed since last check.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {immediate.map((c, i) => <ChangeCard key={`urg-${i}`} change={c} />)}
            {shortTerm.map((c, i) => <ChangeCard key={`st-${i}`} change={c} />)}
            {changes
              .filter((c) => (c.classification?.urgency || "monitor") === "monitor")
              .slice(0, 5)
              .map((c, i) => <ChangeCard key={`mon-${i}`} change={c} />)}
          </div>
        )}
      </section>
    </div>
  );
}
